import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Container, Row, Col, Card, Form, Button, Alert } from 'react-bootstrap';
import { supabase, authService } from '../services/supabase';
import '../styles/login.css';

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sessionReady, setSessionReady] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    // PKCE flow: exchange the code from the URL for a session
    const code = searchParams.get('code');

    if (code) {
      supabase.auth.exchangeCodeForSession(code).then(({ error }) => {
        if (error) {
          setError('El enlace ha expirado o es invalido. Solicita uno nuevo.');
          setInitializing(false);
        } else {
          setSessionReady(true);
          setInitializing(false);
        }
      });
    } else {
      // No code — check if there's already a recovery session
      // (implicit flow or already exchanged)
      supabase.auth.getSession().then(({ data }) => {
        if (data?.session) {
          setSessionReady(true);
        } else {
          setError('No se encontro una sesion de recuperacion valida. Solicita un nuevo enlace.');
        }
        setInitializing(false);
      });
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (password !== confirmPassword) {
      setError('Las contrasenas no coinciden.');
      return;
    }

    if (password.length < 8) {
      setError('La contrasena debe tener al menos 8 caracteres.');
      return;
    }

    setLoading(true);

    try {
      const { success: ok, error } = await authService.updatePassword(password);
      if (error) throw error;
      if (!ok) throw new Error('No se pudo actualizar la contrasena.');

      // Sign out so user logs in fresh with new password
      await authService.signOut();
      setSuccess(true);
      setTimeout(() => navigate('/login', { replace: true }), 3000);
    } catch (err) {
      setError(err.message || 'Error al actualizar la contrasena.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <Container>
        <Row className="justify-content-center align-items-center min-vh-100">
          <Col xs={12} sm={10} md={8} lg={6} xl={5}>
            <Card className="shadow-lg login-card">
              <Card.Body className="p-4 p-sm-5">
                <div className="text-center mb-4">
                  <img
                    src={`${process.env.PUBLIC_URL}/images/logos/Logo_PROMEDIC-07.png`}
                    alt="ProMedic Logo"
                    className="login-logo mb-3"
                  />
                  <h2 className="fw-bold text-primary">Nueva Contrasena</h2>
                  <p className="text-muted">
                    Ingresa tu nueva contrasena para restablecer el acceso.
                  </p>
                </div>

                {initializing ? (
                  <div className="text-center py-4">
                    <span className="spinner-border text-primary me-2" role="status" aria-hidden="true"></span>
                    <p className="text-muted mt-2">Verificando enlace de recuperacion...</p>
                  </div>
                ) : success ? (
                  <Alert variant="success">
                    <i className="bi bi-check-circle me-2"></i>
                    Contrasena actualizada correctamente. Redirigiendo al inicio de sesion...
                  </Alert>
                ) : !sessionReady ? (
                  <>
                    {error && <Alert variant="danger">{error}</Alert>}
                    <div className="text-center">
                      <Link to="/forgot-password" className="btn btn-outline-primary">
                        Solicitar nuevo enlace
                      </Link>
                    </div>
                  </>
                ) : (
                  <>
                    {error && (
                      <Alert variant="danger" dismissible onClose={() => setError(null)}>
                        {error}
                      </Alert>
                    )}

                    <Form onSubmit={handleSubmit}>
                      <Form.Group className="mb-3" controlId="password">
                        <Form.Label>Nueva contrasena</Form.Label>
                        <div className="position-relative">
                          <Form.Control
                            type={showPassword ? 'text' : 'password'}
                            placeholder="Minimo 8 caracteres"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            minLength={8}
                            autoComplete="new-password"
                            disabled={loading}
                            autoFocus
                          />
                          <button
                            type="button"
                            className="password-toggle-btn"
                            onClick={() => setShowPassword(!showPassword)}
                            aria-label={showPassword ? 'Ocultar contrasena' : 'Mostrar contrasena'}
                            disabled={loading}
                          >
                            {showPassword ? (
                              <i className="bi bi-eye-slash"></i>
                            ) : (
                              <i className="bi bi-eye"></i>
                            )}
                          </button>
                        </div>
                      </Form.Group>

                      <Form.Group className="mb-3" controlId="confirmPassword">
                        <Form.Label>Confirmar contrasena</Form.Label>
                        <Form.Control
                          type={showPassword ? 'text' : 'password'}
                          placeholder="Repite tu contrasena"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          required
                          minLength={8}
                          autoComplete="new-password"
                          disabled={loading}
                        />
                      </Form.Group>

                      <Button
                        variant="primary"
                        type="submit"
                        className="w-100 py-2 mb-3"
                        disabled={loading}
                      >
                        {loading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Actualizando...
                          </>
                        ) : (
                          'Restablecer contrasena'
                        )}
                      </Button>
                    </Form>
                  </>
                )}

                <div className="text-center mt-3">
                  <Link to="/login" className="text-muted small">
                    <i className="bi bi-arrow-left me-1"></i>
                    Volver al inicio de sesion
                  </Link>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default ResetPassword;
